import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Footprints,
  AlertTriangle,
  History,
  BookOpen,
  Bell,
  LogOut,
  ShieldCheck,
  ArrowLeft,
  RefreshCw,
  WifiOff,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { audit } from "../lib/audit";
import { appConfig } from "../data/mock";

// Ocorrências e Avisos ocultos temporariamente — reativar quando necessário:
// { to: "/ocorrencias", icon: AlertTriangle, label: "Ocorr."  },
// { to: "/avisos",      icon: Bell,          label: "Avisos"  },
const navItems = [
  { to: "/vigilante",   icon: Home,      label: "Início"    },
  { to: "/rondas",      icon: Footprints, label: "Rondas"   },
  { to: "/historico",   icon: History,   label: "Histórico" },
  { to: "/treinamento", icon: BookOpen,  label: "Treinamento"},
];

export const MobileLayout = ({ children, title, scrollable = false }) => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user, logout, fotoPerfilUrl, avisosNaoLidos } = useApp();

  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const go  = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online",  go);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", go); window.removeEventListener("offline", off); };
  }, []);

  const hora = new Date().getHours();
  const turno = hora >= 22 || hora < 6 ? "Noturno" : hora < 14 ? "Diurno" : "Tarde";
  const hojeLabel = new Date().toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" });

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window.innerWidth < 768;

  if (user?.role === "operacional" && !isMobile) {
    return (
      <div style={{
        minHeight: "100vh", background: "#ffffff", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", padding: "32px", textAlign: "center",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}>
        <div style={{ fontSize: "72px", marginBottom: "24px" }}>📱</div>
        <h1 style={{ fontSize: "22px", fontWeight: "bold", color: "#333A56", marginBottom: "12px" }}>
          Acesso apenas pelo celular
        </h1>
        <p style={{ fontSize: "15px", color: "#64748b", maxWidth: "320px", lineHeight: 1.6 }}>
          O sistema para vigilantes é exclusivo para dispositivos móveis. Acesse pelo seu celular.
        </p>
      </div>
    );
  }

  const iniciais = user?.nome
    ?.split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join("") || "VG";

  return (
    <div
      className="h-[100dvh] overflow-hidden flex flex-col"
      data-testid="mobile-layout"
      style={{ background: "#f8f9fc", color: "#1e293b", fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      {/* Header */}
      <header
        className="flex-none z-40"
        style={{ background: "#08111F", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="px-4 py-3 flex items-center justify-between max-w-md mx-auto lg:max-w-none">
          <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
            {user?.role === "vigilante" ? (
              <img
                src={appConfig.logoUrl}
                alt="Esquematiza"
                style={{ height: "26px", objectFit: "contain", opacity: 0.9, flexShrink: 0 }}
              />
            ) : (
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.2)" }}
              >
                <ShieldCheck className="w-4.5 h-4.5" style={{ color: "#10B981" }} />
              </div>
            )}
          </div>

          <span className="text-[11px] font-medium px-1 text-center" style={{ color: "rgba(255,255,255,0.35)", whiteSpace: "nowrap" }}>
            {hojeLabel} · {turno}
          </span>

          <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
            {user?.role === "vigilante" ? (
              <button
                data-testid="btn-trocar-vigilante"
                onClick={() => {
                  audit({
                    usuario_nome: user?.nome, usuario_role: user?.role,
                    acao: "logout", modulo: "Acesso",
                    descricao: `Troca de vigilante: ${user?.nome} encerrou sessão`,
                  });
                  logout();
                  navigate("/login");
                }}
                className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold transition active:scale-95"
                style={{ border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}
              >
                <RefreshCw className="w-3 h-3" />
                Sair
              </button>
            ) : (
              <button
                data-testid="btn-logout-mobile"
                onClick={() => {
                  audit({
                    usuario_nome: user?.nome, usuario_role: user?.role,
                    acao: "logout", modulo: "Acesso", descricao: "Usuário encerrou sessão",
                  });
                  logout();
                  navigate("/login");
                }}
                className="w-9 h-9 rounded-lg flex items-center justify-center transition"
                style={{ border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {user?.role !== "vigilante" && (
          <div style={{ borderTop: "1px solid rgba(59,130,246,0.15)", background: "rgba(59,130,246,0.07)" }}>
            <div className="px-4 py-2 max-w-md mx-auto lg:max-w-none flex items-center justify-between">
              <span className="text-[12px]" style={{ color: "rgba(147,197,253,0.7)" }}>
                Visualizando como vigilante
              </span>
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-lg transition"
                style={{ color: "#93c5fd", background: "rgba(59,130,246,0.12)" }}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Voltar ao Painel
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Banner offline */}
      {!online && (
        <div className="flex-none flex items-center gap-2 px-4 py-2" style={{ background: "#b45309" }}>
          <WifiOff className="w-3.5 h-3.5 text-white flex-shrink-0" />
          <p className="text-[12px] font-semibold text-white">Sem conexão — algumas ações podem não salvar</p>
        </div>
      )}

      {/* Content */}
      <main
        className={`flex-1 max-w-md mx-auto lg:max-w-none w-full px-4 pt-4 ${
          scrollable ? "overflow-y-auto pb-20" : "overflow-hidden pb-[62px]"
        }`}
      >
        {children}
      </main>

      {/* Bottom nav */}
      <nav
        data-testid="mobile-bottom-nav"
        className="fixed bottom-0 inset-x-0 z-[60]"
        style={{ background: "#08111F", borderTop: "1px solid rgba(255,255,255,0.08)" }}
      >
        <div className="max-w-md mx-auto lg:max-w-none grid grid-cols-4">
          {navItems.map((item) => {
            const Icon  = item.icon;
            const active =
              location.pathname === item.to ||
              (item.to === "/vigilante" && location.pathname === "/");
            const isAvisos = item.to === "/avisos";
            const badge    = isAvisos && avisosNaoLidos > 0;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace("/", "")}`}
                className="flex flex-col items-center justify-center py-3 gap-0.5 transition-colors relative"
                style={{ color: active ? "#ffffff" : "rgba(255,255,255,0.4)" }}
              >
                {active && (
                  <span
                    className="absolute top-0 left-1/2 -translate-x-1/2 rounded-full"
                    style={{ width: 28, height: 2, background: "#60a5fa" }}
                  />
                )}
                <span className="relative">
                  <Icon className="w-5 h-5" strokeWidth={active ? 2.4 : 2} />
                  {badge && (
                    <span
                      className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                      style={{ background: "#ef4444", padding: "0 2px" }}
                    >
                      {avisosNaoLidos > 9 ? "9+" : avisosNaoLidos}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
